import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { Badge } from "@/components/badge";
import type { IQuestion } from "@/apis/exam_papers/types";
import { useUpdateQuestion } from "@/apis/questions/updateQuestion";
import { QueryKeys } from "@/lib/query-keys";

interface CorrectChoiceSelectorProps {
  examPaperId: string;
  question: IQuestion;
}

const getChoiceLabel = (choiceId: string) => choiceId.replace("choice_", "");

const CorrectChoiceSelector: React.FC<CorrectChoiceSelectorProps> = ({
  examPaperId,
  question,
}) => {
  const queryClient = useQueryClient();
  const updateQuestionMutation = useUpdateQuestion();
  const [selectedChoiceId, setSelectedChoiceId] = useState(
    question.correct_choice_id || ""
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedChoiceId(question.correct_choice_id || "");
  }, [question.correct_choice_id]);

  const handleCorrectChoiceChange = async (choiceId: string) => {
    const previousChoiceId = selectedChoiceId;
    if (choiceId === previousChoiceId || isSaving) {
      return;
    }

    setSelectedChoiceId(choiceId);
    setIsSaving(true);

    try {
      await updateQuestionMutation.mutateAsync({
        id: question.id,
        data: { correct_choice_id: choiceId },
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.examPaper.detail(examPaperId),
      });
      message.success("Correct answer updated");
    } catch (error) {
      console.error("Failed to update correct answer:", error);
      setSelectedChoiceId(previousChoiceId);
      message.error("Failed to update correct answer");
    } finally {
      setIsSaving(false);
    }
  };

  if (!question.choices || question.choices.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      {question.choices.map((choice) => {
        const isCorrect = choice.id === selectedChoiceId;

        return (
          <label
            key={choice.id}
            className={`block py-1.5 px-2 rounded-md border transition ${
              isCorrect
                ? "border-green-200 bg-green-50"
                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            } ${isSaving ? "cursor-wait opacity-70" : "cursor-pointer"}`}
          >
            <div className="flex items-start gap-2">
              <input
                type="radio"
                name={`correct-choice-${question.id}`}
                checked={isCorrect}
                disabled={isSaving}
                onChange={() => handleCorrectChoiceChange(choice.id)}
                className="sr-only"
              />
              <span
                className={`mt-0.5 size-4 shrink-0 rounded-full border flex items-center justify-center ${
                  isCorrect
                    ? "border-green-500 bg-green-500"
                    : "border-gray-300 bg-white"
                }`}
                aria-hidden="true"
              >
                {isCorrect && (
                  <span className="size-1.5 rounded-full bg-white" />
                )}
              </span>
              <div className="font-medium min-w-5 text-gray-900">
                {getChoiceLabel(choice.id)}:
              </div>
              <div className="flex-1">
                <div className="inline-flex items-start gap-2">
                  <span
                    className="quill-content text-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: choice.text,
                    }}
                  />
                  {isCorrect && (
                    <Badge color="lime">
                      {isSaving ? "Saving..." : "Correct"}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default CorrectChoiceSelector;
